import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  memo,
} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

import $ from "jquery";
import "jquery/dist/jquery.min.js";
import "datatables.net-dt/js/dataTables.dataTables";
import "datatables.net-dt/css/jquery.dataTables.min.css";

import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Apis } from "api";
import { LanguageContext } from "./../../context/LanguageContext";
import {
  faRefresh,
  faSave,
  faPen,
  faXmark,
  faChevronDown,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IPInput from "components/common/IPInput";

// ---------------------------------------------------------------------------
// Shared toast configuration — avoids repetition in every catch/then block
// ---------------------------------------------------------------------------
const TOAST_CFG = {
  position: "top-center",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "colored",
};

// ---------------------------------------------------------------------------
// RowIPInput — memo-isolated so only the edited row re-renders, not the whole
// table, when a user types or clicks Edit/Save.
// Receives stable callbacks (useCallback with []) from the parent so React
// memo's shallow-equal check reliably prevents unnecessary re-renders.
// ---------------------------------------------------------------------------
const RowIPInput = memo(
  ({
    BID,
    ip,
    isEditing,
    isSaving,
    isNewEntry,
    isEnabled,
    onIPChange,
    onEdit,
    onCancel,
    onSave,
  }) => {
    const hasValidIp = (() => {
      const parts = (ip || "").split(".");
      return (
        parts.length === 4 &&
        parts.every((s) => {
          if (s === "") return false;
          const n = Number(s);
          return Number.isInteger(n) && n >= 0 && n <= 255;
        })
      );
    })();

    // Existing saved rows: Edit button becomes Cancel while editing.
    // Brand-new empty rows: keep Edit disabled and let the user type first.
    const canEdit = isEnabled && !isEditing && !isSaving;
    const canCancel = isEnabled && isEditing && !isSaving && !isNewEntry;
    const canSave = isEnabled && isEditing && hasValidIp && !isSaving;
    const canToggle = canEdit || canCancel;

    const btnStyle = (enabled) => ({
      backgroundColor: enabled ? "#046664" : "#cccccc",
      borderRadius: "5px",
      padding: "8px 12px",
      fontWeight: "bold",
      marginLeft: 4,
      cursor: enabled ? "pointer" : "not-allowed",
      border: "none",
      outline: "none",
    });

    return (
      <div className="inpsumit">
        <IPInput
          value={ip}
          onChange={(val) => onIPChange(BID, val)}
          disabled={!isEnabled || (!isEditing && !isNewEntry) || isSaving}
        />
        <button
          type="button"
          title={canCancel ? "Cancel" : "Edit"}
          disabled={!canToggle}
          onClick={() => (canCancel ? onCancel(BID) : onEdit(BID))}
          style={btnStyle(canToggle)}
        >
          <FontAwesomeIcon
            icon={canCancel ? faXmark : faPen}
            style={{ width: 13, height: 13, color: "white" }}
          />
        </button>
        <button
          type="button"
          title={isSaving ? "Saving…" : "Save"}
          disabled={!canSave}
          onClick={() => onSave(BID)}
          style={btnStyle(canSave)}
        >
          <FontAwesomeIcon
            icon={isSaving ? faCircleNotch : faSave}
            spin={isSaving}
            style={{ width: 13, height: 13, color: "white" }}
          />
        </button>
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// WIPSetupPage
// ---------------------------------------------------------------------------
const WIPSetupPage = () => {
  const { dictionary } = useContext(LanguageContext);

  const [loader, setLoader] = useState(false);
  const [farmList, setFarmList] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [isFarmDropdownOpen, setIsFarmDropdownOpen] = useState(false);
  const [Boothlist, setBoothlist] = useState([]);
  // rowState: { [BID]: { ip: string, isEditing: boolean } }
  const [rowState, setRowState] = useState({});
  const farmDropdownRef = useRef(null);
  const selectedFarmIdRef = useRef("");

  // Refs that give stable callbacks access to latest values without deps
  const rowStateRef = useRef({});
  rowStateRef.current = rowState;

  const dictionaryRef = useRef(dictionary);
  dictionaryRef.current = dictionary;
  selectedFarmIdRef.current = selectedFarmId;

  const selectedFarm =
    farmList.find((farm) => farm.id === selectedFarmId) || null;

  const safeDestroyDataTable = useCallback(() => {
    const tableEl = document.getElementById("ipsetuptable");
    if (!tableEl) return;

    try {
      if ($.fn.DataTable.isDataTable(tableEl)) {
        $(tableEl).DataTable().destroy();
      }
    } catch (error) {
      console.error("DataTable destroy skipped:", error);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Page header
  // ---------------------------------------------------------------------------
  useEffect(() => {
    $(document).ready(function () {
      $(".pageheads").html(dictionary.IPSetup);
    });
  });

  // ---------------------------------------------------------------------------
  // DataTables — re-runs only when Boothlist changes (farm switch or refresh).
  // IP edits update rowState, not Boothlist, so DataTables is never needlessly
  // destroyed and recreated while a user is typing.
  // The cleanup cancels any queued timeout if Boothlist changes again before
  // the 800 ms fires (e.g., rapid farm switching).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (Boothlist.length === 0) return;

    const timer = setTimeout(() => {
      // Hide wrapper so initComplete can reveal it cleanly after each re-init
      $(".ipsetup-blockdis").hide();

      safeDestroyDataTable();

      $("#ipsetuptable").DataTable({
        initComplete: function () {
          $(".ipsetup-blockdis").show();
          setLoader(false);
        },
        stateSave: false, // disabled so pagination resets on farm change
        pagingType: "full_numbers",
        autoWidth: false,
        language: {
          loadingRecords: "&nbsp;",
          paginate: {
            last: '<img src="images/arrow-right_fast.png">',
            first: '<img src="images/arrow-left_fast.png">',
            previous: '<img src="images/arrow-left.png">',
            next: '<img src="images/arrow-right.png">',
          },
          info:
            dictionaryRef.current.Showing +
            " _START_ " +
            dictionaryRef.current.to +
            " _END_ " +
            dictionaryRef.current.of +
            " _TOTAL_ " +
            dictionaryRef.current.entries,
          lengthMenu:
            dictionaryRef.current.show + " _MENU_ " + dictionaryRef.current.entries,
        },
        pageLength: 10,
        // 'f' (filter/search) removed — replaced by the custom farm dropdown
        dom: '<"dt-top-container"<"dt-len" l>r>t<"dt-filter-spacer"><ip>',
        select: { style: "single" },
        fnRowCallback: function (nRow, _aData, _iDisplayIndex, iDisplayIndexFull) {
          $("td:first", nRow).html(iDisplayIndexFull + 1);
          return nRow;
        },
        lengthMenu: [
          [10, 20, 50, 100],
          [10, 20, 50, 100],
        ],
        columnDefs: [
          {
            targets: 0,
            render: function (data, type, _row, meta) {
              return type === "export" ? meta.row + 1 : data;
            },
          },
        ],
      });
    }, 800);

    return () => clearTimeout(timer);
  }, [Boothlist]);

  // ---------------------------------------------------------------------------
  // On mount: load farm list → auto-select first → load booth data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchFarmList();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        farmDropdownRef.current &&
        !farmDropdownRef.current.contains(event.target)
      ) {
        setIsFarmDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------------------------------------------------------------------
  // API: fetch farm dropdown options
  // ---------------------------------------------------------------------------
  const fetchFarmList = () => {
    setIsFarmDropdownOpen(false);
    setLoader(true);
    const loginData = JSON.parse(localStorage.getItem("Login"));
    const uid = loginData.data.UID;
    const requestHeader = { headers: { "Content-Type": "application/json" } };

    axios
      .post(Apis.FarmMastersAdmin, { UID: uid }, requestHeader)
      .then((response) => {
        if (response.data.statuscode == 200) {
          const farms = (response.data.farmArr || response.data.data || []).map(
            (farm) => ({
              id: farm.id || farm.Farm_ID || "",
              farm_name: farm.farm_name || farm.Farm_Name || "",
              location: farm.location || farm.Location || "",
              notes: farm.notes || farm.Notes || "",
              no_of_booths: farm.no_of_booths || farm.No_of_Booths || 0,
            })
          );
          setFarmList(farms);
          if (farms.length > 0) {
            const firstFarmId = farms[0].id;
            setSelectedFarmId(firstFarmId);
            boothList(firstFarmId);
          } else {
            setLoader(false);
          }
        } else {
          setLoader(false);
          toast.error(response.data.message, TOAST_CFG);
        }
      })
      .catch((error) => {
        setLoader(false);
        toast.error(String(error), { ...TOAST_CFG, autoClose: 5000 });
      });
  };

  // ---------------------------------------------------------------------------
  // API: fetch booths for the selected farm via showBoothWeb.
  // Payload: { fid } — the farm id from the dropdown.
  // Used for the initial table load, dropdown changes, and refresh.
  // ---------------------------------------------------------------------------
  const boothList = (farmId) => {
    if (!farmId) return;
    setLoader(true);

    safeDestroyDataTable();

    const requestHeader = { headers: { "Content-Type": "application/json" } };

    axios
      .post(Apis.showBoothWeb, { fid: farmId }, requestHeader)
      .then((response) => {
        if (response.data.statuscode == 200) {
          const list = response.data.data || [];

          setBoothlist(list);

          if (list.length === 0) {
            setLoader(false);
            return;
          }

          // Initialise row-level edit state — active rows without IP start editable
          const initial = {};
          list.forEach((item) => {
            const ipAddress = item.ipaddress || item.IPAddress || "";
            const hasIp = Boolean((ipAddress || "").trim());
            const cameraSerial = item.CameraSerial ?? "";
            const isEnabled =
              String(cameraSerial).trim() !== "" &&
              String(cameraSerial).trim().toLowerCase() !== "null" &&
              String(cameraSerial).trim().toLowerCase() !== "undefined";
            initial[item.BID] = {
              ip: ipAddress,
              originalIp: ipAddress,
              isEditing: isEnabled && !hasIp,
              isNewEntry: isEnabled && !hasIp,
              isSaving: false,
            };
          });
          setRowState(initial);
        } else {
          setLoader(false);
          console.error("showBoothWeb non-200:", response.data.message);
          toast.error(response.data.message, TOAST_CFG);
        }
      })
      .catch((error) => {
        setLoader(false);
        console.error("showBoothWeb error:", error);
        toast.error(String(error), { ...TOAST_CFG, autoClose: 5000 });
      });
  };

  // ---------------------------------------------------------------------------
  // Update the selected farm and refresh the table with the matching
  // showBoothWeb response.
  const handleFarmSelect = (farmId) => {
    setIsFarmDropdownOpen(false);
    setSelectedFarmId(farmId);
    boothList(farmId);
  };

  // ---------------------------------------------------------------------------
  // Row-level handlers — all stable (useCallback with []) so RowIPInput's
  // React.memo comparison never sees a changed callback reference.
  //
  // handleIPChange: uses functional setState — no stale-closure risk.
  // handleEdit:     uses functional setState — no stale-closure risk.
  // handleSave:     reads latest state via rowStateRef; API call uses only
  //                 stable values (module constants + setState setters).
  // ---------------------------------------------------------------------------
  const handleIPChange = useCallback((BID, val) => {
    setRowState((prev) => ({
      ...prev,
      [BID]: { ...prev[BID], ip: val },
    }));
  }, []);

  const handleEdit = useCallback((BID) => {
    setRowState((prev) => ({
      ...prev,
      [BID]: {
        ...prev[BID],
        isEditing: true,
        isNewEntry: false,
        originalIp: prev[BID]?.originalIp ?? prev[BID]?.ip ?? "",
      },
    }));
  }, []);

  const handleCancel = useCallback((BID) => {
    setRowState((prev) => {
      const current = prev[BID] || {};
      const originalIp = current.originalIp ?? "";

      return {
        ...prev,
        [BID]: {
          ...current,
          ip: originalIp,
          isEditing: false,
          isNewEntry: false,
          isSaving: false,
        },
      };
    });
  }, []);

  const handleSave = useCallback((BID) => {
    const row = rowStateRef.current[BID];
    if (!row) return;

    // Validate: 4 segments, each 0–255, none empty
    const segments = (row.ip || "").split(".");
    const valid =
      segments.length === 4 &&
      segments.every((s) => {
        if (s === "") return false;
        const n = Number(s);
        return Number.isInteger(n) && n >= 0 && n <= 255;
      });

    if (!valid) {
      toast.error(
        dictionaryRef.current.ipinvalid || "Invalid IP — each segment must be 0–255",
        { ...TOAST_CFG, autoClose: 3000 }
      );
      return;
    }

    // Row-level saving flag — no global loader (avoids full-page flicker)
    setRowState((prev) => ({
      ...prev,
      [BID]: { ...prev[BID], isSaving: true },
    }));

    const requestHeader = { headers: { "Content-Type": "application/json" } };

    axios
      .post(
        Apis.updateBoothIpaddress,
        { BID, ipaddress: row.ip },
        requestHeader
      )
      .then((response) => {
        if (response.data.statuscode == 200) {
          setRowState((prev) => ({
            ...prev,
            [BID]: {
              ...prev[BID],
              isEditing: false,
              isNewEntry: false,
              isSaving: false,
            },
          }));
          toast.success(
            response.data.message ||
              dictionaryRef.current.ipsaved ||
              "IP address saved",
            TOAST_CFG
          );
          boothList(selectedFarmIdRef.current);
        } else {
          // Non-200 → stay in edit mode so the user doesn't lose their input
          setRowState((prev) => ({
            ...prev,
            [BID]: { ...prev[BID], isSaving: false },
          }));
          console.error("updateBoothIpaddress non-200:", response.data.message);
          toast.error(response.data.message, TOAST_CFG);
        }
      })
      .catch((error) => {
        // Network/server error → stay in edit mode, preserve typed IP
        setRowState((prev) => ({
          ...prev,
          [BID]: { ...prev[BID], isSaving: false },
        }));
        console.error("updateBoothIpaddress error:", error);
        toast.error(String(error), { ...TOAST_CFG, autoClose: 5000 });
      });
  }, []); // stable — reads latest via rowStateRef / dictionaryRef, setState setters are stable

  // ---------------------------------------------------------------------------
  // Table rows — RowIPInput is memo-isolated so only the row being edited
  // triggers a re-render; the rest of the table stays untouched.
  // ---------------------------------------------------------------------------
  const showTable = () => {
    try {
      return Boothlist.map((item, index) => {
        const row = rowState[item.BID] || {
          ip: "",
          originalIp: "",
          isEditing: false,
          isNewEntry: false,
          isSaving: false,
        };
        const cameraSerial = item.CameraSerial ?? "";
        const isEnabled =
          String(cameraSerial).trim() !== "" &&
          String(cameraSerial).trim().toLowerCase() !== "null" &&
          String(cameraSerial).trim().toLowerCase() !== "undefined";

        return (
          <tr key={item.BID}>
            <td style={{ textAlign: "center" }}>{index + 1}</td>
            <td>{item.BID}</td>
            <td>{item.Booth_Name}</td>
            <td>{item.Farm_Name}</td>
            <td>{item.PigID}</td>
            <td>{item.CameraSerial}</td>
            <td>
              <RowIPInput
                BID={item.BID}
                ip={row.ip}
                isEditing={row.isEditing}
                isSaving={row.isSaving}
                isNewEntry={row.isNewEntry}
                isEnabled={isEnabled}
                onIPChange={handleIPChange}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            </td>
          </tr>
        );
      });
    } catch (e) {
      // silently handle render errors
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="pagetp wipsetup-page">
      {/* Loader overlay */}
      <div className="row">
        <div className="col-12">
          {loader && (
            <div className="overlay">
              <div className="overlay__inner">
                <div className="overlay__content">
                  <span className="spinner">
                    <img
                      src={"/images/loader1.gif"}
                      alt=""
                      className="loadersiz"
                    />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar: Farm dropdown (replaces DataTables search) + Refresh */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          position: "relative",
          zIndex: 20,
        }}
      >
        <div
          ref={farmDropdownRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
            overflow: "visible",
          }}
        >
          <label
            style={{
              fontSize: 13,
              color: "#1C2C49",
              fontWeight: 600,
              marginBottom: 0,
              whiteSpace: "nowrap",
            }}
          >
            {dictionary.farmname}:
          </label>
          <div
            style={{
              position: "relative",
              width: 260,
              flexShrink: 0,
              overflow: "visible",
            }}
          >
            <button
              type="button"
              onClick={() => setIsFarmDropdownOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={isFarmDropdownOpen}
              style={{
                width: "100%",
                height: 40,
                borderRadius: 14,
                border: "1px solid rgba(4, 102, 100, 0.22)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,250,0.98) 100%)",
                boxShadow: "0 10px 24px rgba(16, 24, 40, 0.08)",
                padding: "0 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                color: "#1C2C49",
                fontSize: 14,
                fontWeight: 600,
                outline: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFarm ? selectedFarm.farm_name || selectedFarm.id : "Select farm"}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  fontSize: 12,
                  color: "#046664",
                  transition: "transform 0.18s ease",
                  transform: isFarmDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {isFarmDropdownOpen && (
              <div
                role="listbox"
                aria-label={dictionary.farmname}
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  left: 0,
                  right: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  background: "rgba(255, 255, 255, 0.96)",
                  backdropFilter: "blur(14px)",
                  border: "1px solid rgba(4, 102, 100, 0.14)",
                  borderRadius: 16,
                  boxShadow: "0 18px 38px rgba(16, 24, 40, 0.18)",
                  padding: 8,
                  maxHeight: 320,
                  overflowY: "auto",
                  overflowX: "hidden",
                  zIndex: 999,
                }}
              >
                {farmList.length > 0 ? (
                  farmList.map((farm) => {
                    const isSelected = farm.id === selectedFarmId;
                    return (
                      <button
                        key={farm.id}
                        type="button"
                        role="option"
                        onClick={() => handleFarmSelect(farm.id)}
                        style={{
                          width: "100%",
                          border: "none",
                          background: isSelected
                            ? "linear-gradient(90deg, rgba(4,102,100,0.12), rgba(4,102,100,0.04))"
                            : "transparent",
                          borderRadius: 12,
                          padding: "10px 12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          color: "#1C2C49",
                          fontSize: 14,
                          fontWeight: isSelected ? 700 : 500,
                          textAlign: "left",
                          cursor: "pointer",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {farm.farm_name || farm.id}
                        </span>
                        {isSelected && (
                          <span style={{ color: "#046664", fontWeight: 800 }}>✓</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: "12px 14px",
                      color: "#6b7280",
                      fontSize: 13,
                    }}
                  >
                    No farms available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: 10,
            backgroundColor: "#fff",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            title="Refresh"
            onClick={() => boothList(selectedFarmId)}
            style={{
              border: "none",
              background: "transparent",
              padding: 6,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faRefresh} style={{ color: "#046664" }} />
          </button>
        </div>
      </div>

      {Boothlist.length !== 0 && (
        <div>
          <div
            style={{ display: "none" }}
            className="ipsetup-blockdis table-responsive p-0 pb-2"
          >
            <table
              id="ipsetuptable"
              className="table table-striped table-bordered"
            >
              <thead>
                <tr>
                  <th style={{ width: 40 }}>{dictionary.sno}</th>
                  <th style={{ width: 120 }}>{dictionary.boothid}</th>
                  <th style={{ width: 150 }}>{dictionary.boothname}</th>
                  <th style={{ width: 150 }}>{dictionary.farmname}</th>
                  <th style={{ width: 150 }}>{dictionary.pigid}</th>
                  <th style={{ minWidth: 150 }}>{dictionary.macaddress}</th>
                  <th style={{ minWidth: 230 }}>{dictionary.ipaddress}</th>
                </tr>
              </thead>
              <tbody>{showTable()}</tbody>
            </table>
          </div>

          <ToastContainer
            position="bottom-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </div>
      )}
    </div>
  );
};

export default WIPSetupPage;
